'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Sticky header (Figma „Sticky header on scroll“ 60155:19456): the whole header sticks with the top
 * bar pushed above the viewport (negative `top` = top bar height), so only the main row stays.
 * Once it sticks the 1 px bottom line gives way to the dropdown shadow and the row is 84 px like in
 * Figma. The line is drawn inside the padding box (inset shadow) so that panels anchored at
 * `top-full` start under it; when stuck its pixel moves to the margin and nothing below shifts.
 */
export function HeaderShell({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const header = ref.current;
    if (!header) return;
    const update = () => {
      const offset = -parseFloat(getComputedStyle(header).top) || 0;
      setStuck(window.scrollY > offset);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <header
      ref={ref}
      data-site-header
      className={cn(
        'sticky top-0 z-40 bg-white md:-top-11',
        stuck ? 'mb-px shadow-dropdown' : 'pb-px shadow-[inset_0_-1px_#E2E4E7]',
      )}
    >
      {children}
    </header>
  );
}
